import { createApiRef } from '@backstage/core-plugin-api';
import {
  RepositoryFilters,
  RepositoryStats,
  DiscoveryStatus,
  UpdateOwnerRequest,
  GenerateCatalogInfoRequest,
  CreatePullRequestRequest,
  UpdateStatusRequest,
  AiStatus,
  BatchGenerationRequest,
  BatchGenerationJob,
  UnassignOwnerResult,
} from './types';
import {
  CatalogInfoGeneration,
  PullRequest,
  RepositoryOwner,
  UnregisteredRepository,
  UnregisteredRepositoryDetail,
} from '@developer-portal/plugin-unregistered-repos-common';

/**
 * API interface for the unregistered repositories plugin
 */
export interface UnregisteredReposApi {
  // Repository management
  getRepositories(filters?: RepositoryFilters): Promise<UnregisteredRepository[]>;
  getRepository(repoSlug: string): Promise<UnregisteredRepositoryDetail>;
  updateRepositoryStatus(repoSlug: string, request: UpdateStatusRequest): Promise<void>;
  updateRepositoryOwner(repoSlug: string, request: UpdateOwnerRequest): Promise<void>;

  // Owner detection
  detectOwners(repoSlug: string): Promise<RepositoryOwner[]>;
  setPrimaryOwner(repoSlug: string, ownerId: string): Promise<void>;
  unassignOwner(repoSlug: string, ownerId: string, triggerRedetection?: boolean): Promise<UnassignOwnerResult>;

  // Catalog-info generation
  generateCatalogInfo(repoSlug: string, request: GenerateCatalogInfoRequest): Promise<CatalogInfoGeneration>;
  getCatalogInfoGenerations(repoSlug: string): Promise<CatalogInfoGeneration[]>;

  // Pull request management
  createPullRequest(repoSlug: string, request: CreatePullRequestRequest): Promise<PullRequest>;

  // Discovery and statistics
  triggerDiscovery(): Promise<void>;
  getDiscoveryStatus(): Promise<DiscoveryStatus>;
  getRepositoryStats(): Promise<RepositoryStats>;

  // AI service status
  getAiStatus(): Promise<AiStatus>;

  // Batch generation
  createBatchGenerationJob(request: BatchGenerationRequest): Promise<BatchGenerationJob>;
  getBatchGenerationJobs(filters?: {
    status?: string;
    createdBy?: string;
    limit?: number;
    offset?: number;
  }): Promise<BatchGenerationJob[]>;
  getBatchGenerationJob(jobId: string): Promise<BatchGenerationJob>;
  cancelBatchGenerationJob(jobId: string): Promise<void>;
}

export const unregisteredReposApiRef = createApiRef<UnregisteredReposApi>({
  id: 'plugin.unregistered-repos.service',
});
